/**
 * PasskeyEnrollmentPrompt — user portal.
 *
 * One-shot modal shown right after a fresh password login when the user has no
 * passkeys yet (visibility driven by AuthContext.shouldPromptPasskeyEnrollment).
 * Actions: Set up now / Maybe later / Don't ask again.
 *
 * WebAuthn user-activation timing: browsers require navigator.credentials.create()
 * to fire within a tight window of the user's click. If we await registerStart()
 * between the click and startRegistration(), the activation context can go stale
 * and the system prompt silently never appears. So we pre-fetch the registration
 * options as soon as the modal opens, letting "Set up now" call startRegistration()
 * with no intervening await.
 */
import { useEffect, useRef, useState } from 'react';
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';
import { useAuth } from '@/hooks';
import { passkeyService } from '@/api/services';
import { getPasskeyErrorMessage } from '@/lib/errors/passkey-error';
import { Modal } from '@/components/common/Modal/Modal';
import { Button } from '@/components/common/Button/Button';

interface PrefetchedOptions {
  options: Parameters<typeof startRegistration>[0]['optionsJSON'];
  challengeId: string;
}

export function PasskeyEnrollmentPrompt() {
  const { shouldPromptPasskeyEnrollment, dismissPasskeyPrompt, notePasskeyEnrolled } = useAuth();
  const [prefetched, setPrefetched] = useState<PrefetchedOptions | null>(null);
  const [prefetchError, setPrefetchError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [enrolled, setEnrolled] = useState<{ deviceName: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deviceName, setDeviceName] = useState('');
  const cancelledRef = useRef(false);
  const trimmedDeviceName = deviceName.trim();

  // Prefetch registration options when the modal opens (see file-header note).
  useEffect(() => {
    if (!shouldPromptPasskeyEnrollment) return;
    cancelledRef.current = false;
    setPrefetched(null);
    setPrefetchError(null);
    (async () => {
      try {
        const resp = await passkeyService.registerStart();
        if (cancelledRef.current) return;
        setPrefetched({ options: resp.options, challengeId: resp.challengeId });
      } catch (e) {
        if (cancelledRef.current) return;
        setPrefetchError(getPasskeyErrorMessage(e));
      }
    })();
    return () => {
      cancelledRef.current = true;
    };
  }, [shouldPromptPasskeyEnrollment]);

  const handleSetUp = async () => {
    if (!prefetched || !trimmedDeviceName) return;
    setError(null);
    setIsBusy(true);
    try {
      // Synchronous from the click's perspective — no awaits before this.
      const credential = await startRegistration({ optionsJSON: prefetched.options });
      const finishResp = await passkeyService.registerFinish({
        challengeId: prefetched.challengeId,
        credential,
        deviceName: trimmedDeviceName,
      });
      // Mark this device as having a passkey so the nudge won't reappear here.
      notePasskeyEnrolled();
      setEnrolled({ deviceName: finishResp.deviceName });
    } catch (e) {
      setError(getPasskeyErrorMessage(e));
    } finally {
      setIsBusy(false);
    }
  };

  const handleClose = (remember: boolean) => {
    setEnrolled(null);
    setError(null);
    setPrefetched(null);
    setPrefetchError(null);
    setDeviceName('');
    dismissPasskeyPrompt(remember);
  };

  if (!browserSupportsWebAuthn()) return null;

  const setUpDisabled = isBusy || !prefetched || !trimmedDeviceName;

  return (
    <Modal
      isOpen={shouldPromptPasskeyEnrollment}
      onClose={() => handleClose(false)}
      title="Set up a passkey?"
      size="sm"
    >
      {enrolled ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Passkey added as <strong className="text-slate-800 dark:text-white">{enrolled.deviceName}</strong>.
            Next time you can sign in without a password.
          </p>
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => handleClose(false)}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Passkeys let you sign in with your device's fingerprint, face, or screen lock — no
            password to remember. You can manage them later in Account Settings.
          </p>
          <div>
            <label htmlFor="passkey-prompt-device-name" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Device name
            </label>
            <input
              id="passkey-prompt-device-name"
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="e.g. My Phone, Work Laptop"
              required
              disabled={isBusy}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:placeholder-slate-500"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Helps you tell your passkeys apart later — e.g. which one is your phone vs. this
              browser.
            </p>
          </div>
          {isBusy && (
            <div className="rounded-lg border border-teal-300 bg-teal-50 p-2 text-sm text-teal-800 dark:border-teal-700 dark:bg-teal-950/40 dark:text-teal-200">
              Waiting for your browser or device prompt — look for a system dialog asking to use
              your fingerprint, face, or security key.
            </div>
          )}
          {prefetchError && !isBusy && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
              Couldn't prepare passkey setup: {prefetchError}
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          )}
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={() => handleClose(true)} disabled={isBusy}>
              Don't ask again
            </Button>
            <Button variant="secondary" onClick={() => handleClose(false)} disabled={isBusy}>
              Maybe later
            </Button>
            <Button
              variant="primary"
              onClick={handleSetUp}
              loading={isBusy || (!prefetched && !prefetchError)}
              disabled={setUpDisabled}
            >
              Set up now
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default PasskeyEnrollmentPrompt;
